import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { Product } from '@/types';
import { haptic } from '@/lib/haptics';
import { getCategoryIcon, BUILT_IN_CATEGORIES } from '@/lib/constants';

interface InlineRestockFormProps {
  products: Product[];
  onSubmit: (data: { productId: string; quantity: number }) => Promise<void>;
  isLoading?: boolean;
  onAddNewProduct?: () => void;
  onCreateProduct?: (name: string, category: string, quality?: string) => Promise<Product>;
  selectedProductId?: string | null;
  onProductSelected?: () => void;
}

export function InlineRestockForm({ products, onSubmit, isLoading, onAddNewProduct, onCreateProduct, selectedProductId, onProductSelected }: InlineRestockFormProps) {
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState<number>(0);

  // Inline create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductCategory, setNewProductCategory] = useState('FLOWER');
  const [newProductQuality, setNewProductQuality] = useState<string>('PREMIUM');
  const [isCreating, setIsCreating] = useState(false);

  const qualityLevels = [
    { value: 'PREMIUM', label: 'AAA', icon: '💎', color: 'bg-gradient-gold text-dark border-primary shadow-gold' },
    { value: 'HIGH', label: 'Greendoor', icon: '🌿', color: 'bg-green-600 text-white border-green-400 shadow-green-500/30' },
  ];
  const showQuality = newProductCategory === 'FLOWER' || newProductCategory === 'PREROLL';

  // Auto-select product when returning from product creation
  useEffect(() => {
    if (selectedProductId && products.some(p => p.id === selectedProductId)) {
      setProductId(selectedProductId);
      onProductSelected?.();
    }
  }, [selectedProductId, products, onProductSelected]);

  const selectedProduct = products.find(p => p.id === productId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId || quantity <= 0) return;

    await onSubmit({ productId, quantity });
    setProductId('');
    setQuantity(0);
    haptic.success();
  };

  const handleCreateProduct = async () => {
    if (!onCreateProduct || !newProductName.trim()) return;

    setIsCreating(true);
    try {
      const quality = showQuality ? newProductQuality : undefined;
      const product = await onCreateProduct(newProductName.trim(), newProductCategory, quality);
      setProductId(product.id);
      setNewProductName('');
      setNewProductCategory('FLOWER');
      setNewProductQuality('PREMIUM');
      setShowCreateForm(false);
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Quick Product Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Select Product
        </label>
        <select
          value={productId}
          onChange={(e) => {
            haptic.select();
            if (e.target.value === '__add_new__') {
              onAddNewProduct?.();
              setProductId('');
            } else if (e.target.value === '__create_new__') {
              setShowCreateForm(true);
              setProductId('');
            } else {
              setProductId(e.target.value);
              setShowCreateForm(false);
            }
          }}
          className={cn(
            'w-full px-4 py-3 bg-dark-lighter border-2 border-gray-700 rounded-xl',
            'text-white focus:outline-none focus:border-primary transition-all',
            'appearance-none cursor-pointer'
          )}
          required
        >
          <option value="">Choose a product...</option>
          {onAddNewProduct && <option value="__add_new__">➕ Add New Product</option>}
          {!onAddNewProduct && onCreateProduct && <option value="__create_new__">➕ Create New Product</option>}
          {products.map((product) => (
            <option key={product.id} value={product.id}>
              {getCategoryIcon(product.category)} {product.name} ({product.currentStock.toFixed(1)} {product.unitType})
            </option>
          ))}
        </select>
      </div>

      {/* Inline Create Form */}
      {showCreateForm && onCreateProduct && (
        <div className="bg-dark-card rounded-xl p-4 border-2 border-primary/30">
          <h4 className="text-sm font-bold text-primary mb-3">New Product</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Product Name</label>
              <Input
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                placeholder="e.g. Blue Dream 3.5g"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Category</label>
              <select
                value={newProductCategory}
                onChange={(e) => setNewProductCategory(e.target.value)}
                className="w-full px-4 py-3 bg-dark-lighter border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-primary transition-all appearance-none cursor-pointer"
              >
                {BUILT_IN_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>
                    {getCategoryIcon(cat)} {cat}
                  </option>
                ))}
              </select>
            </div>
            {showQuality && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Quality Level</label>
                <div className="grid grid-cols-2 gap-2">
                  {qualityLevels.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => { haptic.select(); setNewProductQuality(level.value); }}
                      className={cn(
                        'flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all active:scale-[0.97] border-2',
                        newProductQuality === level.value
                          ? level.color
                          : 'bg-dark-lighter text-gray-400 border-gray-700/50 hover:border-primary/50'
                      )}
                    >
                      <span>{level.icon}</span>
                      <span>{level.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                variant="ghost"
                size="md"
                onClick={() => { setShowCreateForm(false); setNewProductName(''); setNewProductQuality('PREMIUM'); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreateProduct}
                disabled={!newProductName.trim() || isCreating}
                isLoading={isCreating}
                className="flex-1"
              >
                Create & Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Input */}
      {selectedProduct && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Quantity ({selectedProduct.unitType})
          </label>
          <Input
            type="number"
            step="0.1"
            min="0.1"
            value={quantity || ''}
            onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
            placeholder={`Enter amount in ${selectedProduct.unitType}`}
            required
          />
          {quantity > 0 && (
            <div className="mt-2 px-3 py-2 bg-success/10 border border-success/30 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-300">New stock:</span>
                <span className="font-bold text-success">
                  {(selectedProduct.currentStock + quantity).toFixed(1)} {selectedProduct.unitType}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="success"
        size="md"
        disabled={isLoading || !productId || quantity <= 0}
        isLoading={isLoading}
        className="w-full"
      >
        {quantity > 0 ? `Add +${quantity.toFixed(1)} to Stock` : 'Add to Stock'}
      </Button>
    </form>
  );
}
